/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import { ConstraintViolation } from '@shared/models/constraint-violation';

export function extractHttpErrorMessage(error: HttpErrorResponse| Error): string {
  let errorMessage = error.message;

  if (error instanceof HttpErrorResponse) {
    if (error.error.hasOwnProperty('error_description')) {
      errorMessage = error.error.error_description;
    }
  }
  return errorMessage;
}

export function extractConstraintViolations(error: HttpErrorResponse | Error): ConstraintViolation[] {
  if (false === error instanceof HttpErrorResponse || !Array.isArray(error.error?.violations)) {
    return [];
  }

  return error.error.violations.map(violation => ({
    propertyPath: violation.propertyPath,
    message: violation.message
  }));
}

/** Uebersetzungsfunktion, wie sie TranslateService.instant bereitstellt. */
export type Translator = (key: string, params?: object) => string;

/**
 * Baut den Hinweis fuer einen gesperrten Speichern-Button: welche Pflichtfelder noch leer
 * sind und welche einen ungueltigen Wert enthalten. Ohne diesen Hinweis ist an einem
 * ausgegrauten Button nicht erkennbar, was noch fehlt.
 *
 * `labels` bildet Formularfelder auf ihre Uebersetzungsschluessel ab - Felder, die dort
 * nicht stehen, bleiben unerwaehnt und fallen auf den allgemeinen Text zurueck. Ist das
 * Formular gueltig, kommt ein leerer String zurueck; Material zeigt dann keinen Tooltip.
 */
export function invalidFieldsHint(
  form: AbstractControl,
  labels: Record<string, string>,
  translate: Translator
): string {
  if (form.valid) {
    return '';
  }

  const emptyFields: string[] = [];
  const invalidFields: string[] = [];

  Object.keys(labels).forEach(field => {
    const control = form.get(field);

    if (!control || control.valid) {
      return;
    }

    const label = translate(labels[field]);

    if (control.hasError('required')) {
      emptyFields.push(label);
    } else {
      invalidFields.push(label);
    }
  });

  const hints: string[] = [];

  if (emptyFields.length > 0) {
    hints.push(translate('core.requiredFieldsMissing', { fields: emptyFields.join(', ') }));
  }

  if (invalidFields.length > 0) {
    hints.push(translate('core.invalidFields', { fields: invalidFields.join(', ') }));
  }

  return hints.length > 0 ? hints.join(' · ') : translate('core.formIncomplete');
}

/**
 * Baut aus der Hex-Farbe einer Abteilung und ihrer Deckkraft in Prozent einen
 * CSS-Farbwert fuer die Blockhinterlegung. Unzugeordnete Personen und ungueltige
 * Farbwerte fallen auf ein neutrales Grau zurueck, damit die Liste nie ohne
 * Hinterlegung dasteht.
 */
export function departmentTint(color: string | undefined, opacityInPercent: number | undefined): string {
  const NEUTRAL_GREY = [189, 189, 189];
  const match = /^#([0-9A-Fa-f]{6})$/.exec(color ?? '');
  const [red, green, blue] = match
    ? [0, 2, 4].map(offset => parseInt(match[1].substring(offset, offset + 2), 16))
    : NEUTRAL_GREY;
  const alpha = Math.min(100, Math.max(0, opacityInPercent ?? 0)) / 100;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function incrementDate(date: Date): Date {
  const incrementedDate = new Date(date);
  incrementedDate.setDate(date.getDate() + 1);
  return incrementedDate;
}

export function decrementDate(date: Date): Date {
  const decrementedDate = new Date(date);
  decrementedDate.setDate(date.getDate() - 1);
  return decrementedDate;
}
